import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useStore } from "../lib/store";
import { useSEO } from "../lib/seo";
import Reveal from "../components/Reveal";
import { Mail, Phone, Clock, ChevronDown } from "lucide-react";

function PageHead({ title, intro }) {
  return (
    <section className="bg-sage/40 pt-12 pb-10">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <h1 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl">{title}</h1>
        {intro && <p className="text-charcoal/70 mt-3 leading-relaxed">{intro}</p>}
      </div>
    </section>
  );
}

export function Contact() {
  const { settings } = useStore();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  useSEO({ title: "Contact us — Haven Hives", description: "Write to the Hive — we reply within two working days." });
  const c = settings?.contact || {};

  const submit = async (e) => {
    e.preventDefault(); setError("");
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch { setError("Please check your details and try again."); }
  };

  return (
    <div data-testid="contact-page">
      <PageHead title="Say hello to the hive" intro="Questions about an order, a product, or planning school gifting? We'd love to help." />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 grid sm:grid-cols-2 gap-8">
        <div className="space-y-4 text-sm">
          {c.email && <p className="flex items-center gap-2.5"><Mail className="w-4 h-4 text-forest" /> <a className="font-semibold underline underline-offset-4" href={`mailto:${c.email}`}>{c.email}</a></p>}
          {c.phone && <p className="flex items-center gap-2.5"><Phone className="w-4 h-4 text-forest" /> {c.phone}</p>}
          {c.hours && <p className="flex items-center gap-2.5"><Clock className="w-4 h-4 text-forest" /> {c.hours}</p>}
          {c.note && <p className="text-xs text-charcoal/50 bg-butter/40 rounded-2xl p-3.5 leading-relaxed">{c.note}</p>}
          <p className="text-xs text-charcoal/50">For order questions, Track Order is the fastest — it shows live status and cancellation options.</p>
        </div>
        {done ? (
          <div className="bg-white rounded-3xl card-outline p-6 text-center" data-testid="contact-success">
            <p className="font-display font-semibold text-xl">Message received</p>
            <p className="text-sm text-charcoal/70 mt-2">We'll reply within 2 working days.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-3xl card-outline p-5 space-y-4" data-testid="contact-form">
            <div>
              <label htmlFor="c-name" className="text-xs font-bold uppercase tracking-widest text-charcoal/60">Your name *</label>
              <input id="c-name" required value={form.name} data-testid="contact-name-input" onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5 w-full border border-charcoal/15 rounded-2xl px-4 py-3 text-sm" />
            </div>
            <div>
              <label htmlFor="c-email" className="text-xs font-bold uppercase tracking-widest text-charcoal/60">Email *</label>
              <input id="c-email" type="email" required value={form.email} data-testid="contact-email-input" onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="mt-1.5 w-full border border-charcoal/15 rounded-2xl px-4 py-3 text-sm" />
            </div>
            <div>
              <label htmlFor="c-msg" className="text-xs font-bold uppercase tracking-widest text-charcoal/60">Message *</label>
              <textarea id="c-msg" required rows={4} value={form.message} data-testid="contact-message-input" onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} className="mt-1.5 w-full border border-charcoal/15 rounded-2xl px-4 py-3 text-sm" />
            </div>
            {error && <p role="alert" className="text-xs text-coral">{error}</p>}
            <button type="submit" className="w-full bg-forest text-white hover:bg-forest-dark py-3.5 rounded-full" data-testid="contact-submit">Send message</button>
          </form>
        )}
      </div>
    </div>
  );
}

export function FAQ() {
  const [faqs, setFaqs] = useState([]);
  const [open, setOpen] = useState(0);
  useSEO({ title: "FAQs — Haven Hives", description: "Answers about delivery, returns, COD and more." });
  useEffect(() => { api.get("/settings").then((r) => setFaqs(r.data.faqs || [])); }, []);
  return (
    <div data-testid="faq-page">
      <PageHead title="Frequently asked questions" intro="Short, honest answers. Anything we've missed — write to us." />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 space-y-3">
        {faqs.map((f, i) => (
          <div key={i} className="bg-white rounded-2xl card-outline" data-testid={`faq-item-${i}`}>
            <button onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left text-sm font-semibold">
              {f.q} <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
            </button>
            {open === i && <p className="px-5 pb-4 text-sm text-charcoal/75 leading-relaxed">{f.a}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function Policy() {
  const { slug } = useParams();
  const [policies, setPolicies] = useState({});
  useSEO({ title: "Policy — Haven Hives", description: "Haven Hives store policy." });
  useEffect(() => { api.get("/settings").then((r) => setPolicies(r.data.policies || {})); }, []);
  const p = policies[slug];
  if (!p) return <div className="max-w-3xl mx-auto px-5 py-16" aria-busy="true"><div className="h-40 rounded-3xl bg-sage/20 animate-pulse" /></div>;
  return (
    <div data-testid={`policy-${slug}-page`}>
      <PageHead title={p.title} />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 space-y-4 text-charcoal/80 leading-relaxed whitespace-pre-line">{p.body}</div>
    </div>
  );
}

import { useParams } from "react-router-dom";

export function About() {
  useSEO({ title: "Our story — Haven Hives", description: "Why we make joyful everyday products for kids." });
  return (
    <div data-testid="about-page">
      <PageHead title="Little things, big happiness." intro="Haven Hives is a home for joyful, dependable everyday products for children — bottles, bento boxes, kawaii stationery and little trolleys for big adventures." />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 space-y-4 text-charcoal/80 leading-relaxed">
        <p>We started with a simple thought: the objects children carry every day shape how they feel about their day. A bottle that's easy to open. A lunchbox that keeps the fruit away from the roti. A pen that makes homework feel like play.</p>
        <p>So we design and curate with care — warm colours, friendly shapes, materials chosen thoughtfully, and specifications we're honest about. If a claim isn't in the product data, we won't print it on the box.</p>
        <p>Everything here is built to be loved daily and gifted happily. Welcome to the hive.</p>
      </div>
    </div>
  );
}

export function PolicyPage({ slug }) {
  const [policies, setPolicies] = useState({});
  useSEO({ title: "Policy — Haven Hives", description: "Haven Hives store policy." });
  useEffect(() => { api.get("/settings").then((r) => setPolicies(r.data.policies || {})); }, []);
  const p = policies[slug];
  if (!p) return <div className="max-w-3xl mx-auto px-5 py-16" aria-busy="true"><div className="h-40 rounded-3xl bg-sage/20 animate-pulse" /></div>;
  return (
    <div data-testid={`policy-${slug}-page`}>
      <PageHead title={p.title} />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 space-y-4 text-charcoal/80 leading-relaxed whitespace-pre-line">{p.body}</div>
    </div>
  );
}

export { PageHead };
